import React, { useState, useRef, useEffect } from 'react';
import platform from 'platform';
import Http from '../../../services/Http';
import useToast from "../../../hooks/Toast";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBackIosNew";
import dataService from '../../../services/data.service';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DoNotDisturbAltRoundedIcon from '@mui/icons-material/DoNotDisturbAltRounded';
import {
  TextField,
  Button,
  Typography,
  IconButton,
  Box,
  Dialog,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  MenuItem
} from '@mui/material';
import { styled } from '@mui/system';
import { Icon } from '@iconify/react';
import { useLocation } from 'react-router-dom';
import LoadingScreen from '../LoadingScreen';
import { useHMG } from '../../../HMGContext';

const Container = styled(Box)({
  position: 'relative',
  width: 460,
  maxWidth: '92vw',
  backgroundColor: '#fff',
  borderRadius: 12,
  boxShadow: '0px 2px 10px rgba(0,0,0,0.05)',
});

const Actions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '14px 20px',
  borderTop: '1px solid #E3E8EF',
});

// Feedback types the user chooses in step 1. `id` is sent to the backend as feedbackType
// and drives routing: bug -> memory + code; confusion/requirement -> memory (docs) only.
const FEEDBACK_TYPES = [
  {
    id: 'bug',
    title: "Something isn't working",
    desc: 'A bug or error — a feature behaved incorrectly or failed.',
    icon: 'solar:bug-minimalistic-linear',
    tone: '#B76E00',
    toneBg: '#FFF4E5',
  },
  {
    id: 'confusion',
    title: 'Confused, or have feedback',
    desc: 'Something is unclear or hard to use, or a suggestion to improve it.',
    icon: 'solar:question-circle-linear',
    tone: '#1160C4',
    toneBg: '#E9F2FF',
  },
  {
    id: 'requirement',
    title: 'Request something new',
    desc: 'A new capability or change you would like the product to support.',
    icon: 'solar:add-circle-linear',
    tone: '#0E7A4B',
    toneBg: '#E7F6EE',
  },
];

const FUNCTIONS = [
  'Plan editing & line items',
  'Drivers & calculations',
  'Chart of Accounts & mapping',
  'Reports & exports',
  'Data migration',
  'Critique',
  'Settings & preferences',
  'Other',
];

// Two-step flow: pick the type, then give the details.
const STEP_LABELS = ['Type', 'Details'];
const TOTAL_STEPS = STEP_LABELS.length;

const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-matroska',
];
const MAX_SIZE_MB = 10;

const FeedbackWidget = ({ isOpen, setShowFeedbackWidget }) => {
  // Wizard state
  const [step, setStep] = useState(0);
  const [feedbackType, setFeedbackType] = useState(null);
  const [functionality, setFunctionality] = useState('');
  const [functionalityOther, setFunctionalityOther] = useState('');

  // Existing fields
  const [selectedRating, setSelectedRating] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const fileInputRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [info, setInfo] = useState({ browser: '', os: '' });
  const [location, setLocation] = useState(null);
  const { Toast, showToast } = useToast();
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);

  // Company context: optional "this is about a specific company (property)".
  const [isCompanySpecific, setIsCompanySpecific] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);

  const routeLocation = useLocation();
  // Plan is attached silently when feedback is opened from inside a plan — no user step for it.
  const planId = routeLocation.pathname.includes("/planVersion") ? routeLocation.pathname.split("/").pop() : null;
  const critiqueId = routeLocation.pathname.includes("/critique/details/") ? routeLocation.pathname.split("/").pop() : null;
  const { currentModalName, selectedHMG } = useHMG();
  const pageName = currentModalName || (routeLocation.pathname.includes("/planVersion") ? "Plan Details" : routeLocation.pathname.includes("/ChartOfAccounts") ? "Chart of account management" : routeLocation.pathname.includes("/settings") ? "Settings" : routeLocation.pathname.includes("/critique") ? "Critique" : "All plans");

  const propertyOptions = React.useMemo(() => {
    try {
      return dataService.getPropertiesForHMG();
    } catch (e) {
      return [];
    }
  }, []);
  const resolvedHmgName = React.useMemo(() => {
    if (selectedHMG?.name) return selectedHMG.name;
    try {
      return dataService.getSelectedHMG()?.name || null;
    } catch (e) {
      return null;
    }
  }, [selectedHMG]);

  const onClose = () => {
    setShowFeedbackWidget?.(false);
  };

  useEffect(() => {
    const fetchEnvData = async () => {
      const browser = platform.name || 'Unknown Browser';
      const os = platform.os ? platform.os.toString() : 'Unknown OS';
      let locationData = null;
      try {
        const response = await fetch('https://ipinfo.io/json');
        if (response.ok) {
          locationData = await response.json();
        }
      } catch (err) {
        console.error('Error fetching location:', err);
      }
      setInfo({ browser, os });
      setLocation(locationData);
    };
    fetchEnvData();
  }, []);

  // ---- Step validation & navigation ----------------------------------------
  // Step 0 (Type): a type must be chosen. Step 1 (Details): only the message is required;
  // area, company and rating are optional.
  const canAdvance = () => {
    switch (step) {
      case 0: return !!feedbackType;
      case 1: return feedbackText.trim().length > 0;
      default: return false;
    }
  };

  const goNext = () => {
    if (step === 0) {
      if (!feedbackType) return;
      setStep(1);
      return;
    }
    // step 1 — submit
    if (!feedbackText.trim()) {
      setShowValidationError(true);
      return;
    }
    submitEvent();
  };
  const goBack = () => { if (step > 0) setStep(step - 1); };

  const areaLabel = functionality === 'Other' ? (functionalityOther.trim() || 'Other') : functionality;

  // Type-adaptive copy for the Details step.
  const detailCopy = feedbackType === 'requirement'
    ? { q: 'Describe what you need', sub: 'What should it do, and what problem would it solve?', lab: 'Requirement', ph: 'Describe the capability you would like…' }
    : feedbackType === 'confusion'
      ? { q: "Tell us what's unclear", sub: 'What were you trying to do, and where did it get confusing?', lab: 'Feedback', ph: 'Describe what confused you or your suggestion…' }
      : { q: 'Tell us what happened', sub: 'Steps you took, what you expected, and what you saw.', lab: 'What went wrong', ph: 'Describe the issue…' };

  const handleAreaChange = (e) => {
    const value = e.target.value;
    setFunctionality(value);
    if (value !== 'Other') setFunctionalityOther('');
  };

  const handleCompanyToggle = (e) => {
    const checked = e.target.checked;
    setIsCompanySpecific(checked);
    if (!checked) setSelectedPropertyId(null);
  };

  // ---- File handling (unchanged behaviour) ---------------------------------
  const processFiles = (rawFiles) => rawFiles.map((file, index) => {
    const fileId = Date.now() + index;
    let status = 'uploaded';
    let errorMessage = '';
    if (!ALLOWED_TYPES.includes(file.type)) {
      status = 'error';
      errorMessage = 'Unsupported file type.';
    } else if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      status = 'error';
      errorMessage = 'File exceeds maximum size.';
    }
    return { file, fileId, status, errorMessage, name: file.name, size: file.size, type: file.type };
  });

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    setUploadedFiles((prev) => [...prev, ...processFiles(newFiles)]);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    setUploadedFiles((prev) => [...prev, ...processFiles(droppedFiles)]);
    if (e.target && e.target.value !== undefined) {
      e.target.value = '';
    }
  };

  const handleFileDelete = (indexToDelete) => {
    setUploadedFiles((prev) => prev.filter((_, index) => index !== indexToDelete));
  };

  const handleFeedbackTextChange = (e) => {
    setFeedbackText(e.target.value);
    if (showValidationError && e.target.value.trim()) {
      setShowValidationError(false);
    }
  };

  // ---- Submit ---------------------------------------------------------------
  const submitEvent = async () => {
    if (!feedbackText?.trim()) {
      setShowValidationError(true);
      setStep(1);
      return;
    }
    setIsLoading(true);
    const loc = location || {};
    const payload = {
      browser: info.browser,
      os: info.os,
      ip: loc.ip,
      city: loc.city,
      region: loc.region,
      country: loc.country,
      loc: loc.loc,
      postal: loc.postal,
      timezone: loc.timezone,
      selectedRating: selectedRating,
      pageName: pageName,
      feedbackText: feedbackText,
      hmgId: dataService.getSelectedHMGId(),
      hmgName: resolvedHmgName,
      planId: planId,
      critiqueId: critiqueId,
      propertyId: isCompanySpecific ? selectedPropertyId : null,
      propertyName: isCompanySpecific ? (propertyOptions.find((o) => o.value === selectedPropertyId)?.label || null) : null,
      // User-selected type + functionality area drive backend routing.
      feedbackType: feedbackType,
      functionality: areaLabel || null,
    };

    const formData = new FormData();
    formData.append("feedback", new Blob([JSON.stringify(payload)], { type: "application/json" }));
    uploadedFiles
      .filter(file => file.status !== 'error')
      .forEach(file => { formData.append("uploadedFiles", file.file); });

    try {
      Http.POST("/feedback/save", formData).then(
        (response) => {
          setIsLoading(false);
          if (response && response.status >= 200 && response.status < 300) {
            setFeedbackSubmitted(true);
          } else {
            showToast("Error while submitting feedback.", "error");
          }
        }).catch((err) => {
          setIsLoading(false);
          console.error("Error submitting feedback:", err);
          showToast("Error while submitting feedback.", "error");
        });
    } catch (err) {
      setIsLoading(false);
      console.error("Error submitting feedback:", err);
      showToast("Error while submitting feedback.", "error");
    }
  };

  // ---- Renderers ------------------------------------------------------------
  const renderTypeStep = () => (
    <>
      <Typography sx={{ fontSize: 15, fontWeight: 650, color: '#212B36' }}>What would you like to tell us?</Typography>
      <Typography sx={{ fontSize: 12.5, color: '#637381', mb: 1.5 }}>This sets how we investigate and where the ticket is routed.</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {FEEDBACK_TYPES.map((t) => {
          const active = feedbackType === t.id;
          return (
            <Box
              key={t.id}
              role="button"
              tabIndex={0}
              onClick={() => setFeedbackType(t.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFeedbackType(t.id); } }}
              sx={{
                display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.5, cursor: 'pointer',
                borderRadius: '10px',
                border: `1.5px solid ${active ? t.tone : '#E3E8EF'}`,
                backgroundColor: active ? t.toneBg : '#fff',
                boxShadow: active ? `0 0 0 1px ${t.tone} inset` : 'none',
                transition: 'all .15s',
                '&:hover': { borderColor: active ? t.tone : '#CDD5E0' },
              }}
            >
              <Box sx={{ width: 34, height: 34, flex: 'none', borderRadius: '8px', backgroundColor: t.toneBg, color: t.tone, display: 'grid', placeItems: 'center' }}>
                <Icon icon={t.icon} style={{ fontSize: 19 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 650, color: '#212B36' }}>{t.title}</Typography>
                <Typography sx={{ fontSize: 12.5, color: '#637381' }}>{t.desc}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </>
  );

  const renderDetailsStep = () => (
    <>
      <Typography sx={{ fontSize: 15, fontWeight: 650, color: '#212B36' }}>{detailCopy.q}</Typography>
      <Typography sx={{ fontSize: 12.5, color: '#637381', mb: 1.5 }}>{detailCopy.sub}</Typography>

      {/* Area — optional dropdown */}
      <TextField
        select fullWidth size="small" sx={{ mb: 1.5 }}
        label="Which area? (optional)"
        value={functionality}
        onChange={handleAreaChange}
      >
        <MenuItem value=""><em>Not sure / not listed</em></MenuItem>
        {FUNCTIONS.map((name) => (
          <MenuItem key={name} value={name}>{name}</MenuItem>
        ))}
      </TextField>
      {functionality === 'Other' && (
        <TextField
          fullWidth size="small" sx={{ mb: 1.5 }}
          label="Tell us which area"
          placeholder="Describe the area or screen"
          value={functionalityOther}
          onChange={(e) => setFunctionalityOther(e.target.value)}
          inputProps={{ maxLength: 80 }}
        />
      )}

      {/* Company — optional */}
      <FormControlLabel
        sx={{ ml: 0 }}
        control={<Checkbox size="small" checked={isCompanySpecific} onChange={handleCompanyToggle} sx={{ py: 0.5 }} />}
        label={<Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#212B36' }}>This is specific to one company</Typography>}
      />
      {isCompanySpecific && (
        <Autocomplete
          size="small"
          sx={{ mt: 1.25, mb: 1.5 }}
          options={propertyOptions}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(option, val) => option.value === val.value}
          value={propertyOptions.find((o) => o.value === selectedPropertyId) || null}
          onChange={(_, val) => setSelectedPropertyId(val ? val.value : null)}
          renderInput={(params) => (<TextField {...params} label="Which company?" placeholder="Select company" />)}
        />
      )}

      {/* Rating — optional */}
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#212B36', mt: 0.5, mb: 0.5 }}>
        How would you rate this experience? <span style={{ color: '#919EAB', fontWeight: 400 }}>(optional)</span>
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        {[1, 2, 3, 4, 5].map((num) => {
          const active = selectedRating === num;
          return (
            <Button
              key={num}
              onClick={() => setSelectedRating(active ? null : num)}
              sx={{
                minWidth: 36, width: 36, height: 36, p: 0, borderRadius: '50%', fontWeight: 650, fontSize: 14,
                border: `1.5px solid ${active ? '#2A5BA2' : '#E3E8EF'}`,
                backgroundColor: active ? '#EAF0F9' : '#fff',
                color: active ? '#2A5BA2' : '#637381',
                '&:hover': { backgroundColor: active ? '#EAF0F9' : '#F7F9FC' },
              }}
            >
              {num}
            </Button>
          );
        })}
      </Box>

      {/* Message — required */}
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#212B36', mb: 0.5 }}>{detailCopy.lab} *</Typography>
      <TextField
        fullWidth variant="outlined" multiline rows={4}
        placeholder={detailCopy.ph}
        value={feedbackText}
        onChange={handleFeedbackTextChange}
        inputProps={{ maxLength: 1000 }}
        error={showValidationError}
        helperText={
          <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box component="span" sx={{ color: '#FF5630' }}>{showValidationError ? 'This field is required.' : ''}</Box>
            <Box component="span" sx={{ color: '#637381' }}>{`${feedbackText.length}/1000`}</Box>
          </Box>
        }
        sx={{ mb: 1.5, '.MuiFormHelperText-root': { marginLeft: 0, marginRight: 0 } }}
      />

      {/* Upload */}
      <Box
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={() => setIsDragging(true)}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        sx={{
          border: `1.5px dashed ${isDragging ? '#1877F2' : '#CDD5E0'}`, borderRadius: '9px', backgroundColor: '#F7F9FC',
          p: 2, textAlign: 'center', fontSize: 12.5, color: '#637381', cursor: 'pointer',
        }}
      >
        Drop a screenshot or video here, or <span style={{ color: '#1877F2' }}>choose a file</span> · max 10MB
        <input type="file" accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.csv,image/*,video/*" multiple hidden ref={fileInputRef} onChange={handleFileSelect} />
      </Box>

      {uploadedFiles.map((file, index) => (
        <Box key={file.fileId} sx={{ mt: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon icon="solar:file-send-linear" style={{ color: '#1C274C', fontSize: 16 }} />
            <Typography sx={{ flex: 1, minWidth: 0, color: '#212B36', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</Typography>
            <IconButton onClick={() => handleFileDelete(index)} size="small" sx={{ p: 0.5 }}>
              <ClearIcon sx={{ color: '#637381', fontSize: 16 }} />
            </IconButton>
          </Box>
          {file.status === 'uploaded' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: '22px' }}>
              <CheckCircleRoundedIcon sx={{ fontSize: 14, color: '#118D57' }} />
              <Typography sx={{ color: '#118D57', fontSize: 10, fontWeight: 500 }}>File uploaded.</Typography>
            </Box>
          )}
          {file.status === 'error' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: '22px' }}>
              <DoNotDisturbAltRoundedIcon sx={{ fontSize: 14, color: '#FF5630' }} />
              <Typography sx={{ color: '#FF5630', fontSize: 10, fontWeight: 500 }}>{file.errorMessage}</Typography>
            </Box>
          )}
        </Box>
      ))}
    </>
  );

  const renderStep = () => {
    switch (step) {
      case 0: return renderTypeStep();
      case 1: return renderDetailsStep();
      default: return null;
    }
  };

  return (
    <Dialog open={isOpen}>
      <Toast />
      <Container>
        {feedbackSubmitted ? (
          <>
            <IconButton onClick={onClose} sx={{ position: 'absolute', top: 12, right: 12, p: 0, width: 24, height: 24 }}>
              <ClearIcon sx={{ width: 22, height: 22 }} />
            </IconButton>
            <Box sx={{ width: '100%', minHeight: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center' }}>
              <Box sx={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: '#E7F6EE', color: '#0E7A4B', display: 'grid', placeItems: 'center', mb: 2 }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 40 }} />
              </Box>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#212B36', mb: 0.5 }}>Thanks for your feedback!</Typography>
              <Typography sx={{ fontSize: 13, color: '#637381', maxWidth: 320 }}>Your feedback has been submitted.</Typography>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '16px 20px 12px', borderBottom: '1px solid #E3E8EF' }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#212B36' }}>Share feedback</Typography>
              <IconButton onClick={onClose} size="small"><ClearIcon sx={{ width: 20, height: 20 }} /></IconButton>
            </Box>

            {isLoading ? (
              <Box sx={{ width: '100%', height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LoadingScreen title='Submitting your feedback.' />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', gap: 0.75, p: '12px 20px 0' }}>
                  {STEP_LABELS.map((_, idx) => (
                    <Box key={idx} sx={{ flex: 1, height: 4, borderRadius: '3px', backgroundColor: idx <= step ? '#2A5BA2' : '#E3E8EF', transition: 'background .25s' }} />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', p: '10px 20px 0' }}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#919EAB' }}>{STEP_LABELS[step]}</Typography>
                  <Typography sx={{ fontSize: 11, color: '#919EAB' }}>{`Step ${step + 1} of ${TOTAL_STEPS}`}</Typography>
                </Box>

                <Box sx={{ p: '14px 20px 6px', minHeight: 300, maxHeight: '58vh', overflowY: 'auto' }}>
                  {renderStep()}
                </Box>

                <Actions>
                  <Button
                    onClick={goBack}
                    startIcon={<ArrowBackIcon sx={{ fontSize: '12px !important' }} />}
                    sx={{ textTransform: 'none', color: '#637381', fontWeight: 650, visibility: step === 0 ? 'hidden' : 'visible' }}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={goNext}
                    disabled={!canAdvance()}
                    variant="contained"
                    sx={{
                      textTransform: 'none', fontWeight: 650, borderRadius: '9px', px: 2, py: 1,
                      backgroundColor: '#212B36', '&:hover': { backgroundColor: '#2A5BA2' },
                      '&.Mui-disabled': { backgroundColor: '#919EAB33', color: '#919EABCC' },
                    }}
                  >
                    {step === TOTAL_STEPS - 1 ? 'Share feedback' : 'Continue'}
                  </Button>
                </Actions>
              </>
            )}
          </>
        )}
      </Container>
    </Dialog>
  );
};

export default FeedbackWidget;
